import { TenantType } from '@xeetr/types/enums'
import {
  BaseBookingModel,
  BookingCustomField,
  FoodBookingModel,
  ServiceBookingModel,
} from '@xeetr/types/models'
import { useMemo } from 'react'

import StatisticsConfigButton from './StatisticsConfigButton'
import { Badge } from '@/components/ui/badge'

import { useBookings } from '@/hooks/useBookings'
import { useDashboard } from '@/hooks/useDashboard'
import { useTenant } from '@/hooks/useTenant'
import { useFilteredBookings } from '@/hooks/useFilteredBookings'

const StatisticSummary = () => {
  const { showStatistics, statisticsConfig } = useDashboard()
  const { isUpdating } = useBookings()
  const { bookings } = useFilteredBookings()
  const { tenant } = useTenant()

  const stats = useMemo(() => {
    if (!bookings || !tenant) return null

    const bookingData = bookings
    const totalBookings = bookingData.length
    const customFieldCounts: Record<string, number> = {}

    let totalPeople = 0
    let totalTables = 0

    // Find Catering field ID
    const cateringFieldId = tenant.bookingCustomFields?.find(
      (field) => field.name === 'Catering'
    )?.id

    // table size breakdown (1er, 2er etc) for food tenants
    const tableSizeCounts: Record<number, number> = {}

    if (tenant.type === TenantType.FOOD) {
      bookingData.forEach((booking: BaseBookingModel) => {
        const foodBooking = booking as FoodBookingModel
        const isCatering = cateringFieldId
          ? Boolean(foodBooking.bookingAdditions?.customFields?.[cateringFieldId])
          : false
        totalPeople += isCatering ? 0 : foodBooking.amount
        totalTables += foodBooking.tables.length
        if (foodBooking.state?.status !== 'EVENT' && !isCatering) {
          tableSizeCounts[foodBooking.amount] = (tableSizeCounts[foodBooking.amount] || 0) + 1
        }

        // Aggregate custom fields
        if (foodBooking.bookingAdditions?.customFields) {
          Object.entries(foodBooking.bookingAdditions.customFields).forEach(([key, value]) => {
            const fieldType = tenant.bookingCustomFields?.find((field) => field.id === key)?.type
            if (fieldType === 'number') {
              customFieldCounts[key] = (customFieldCounts[key] || 0) + Number(value)
            } else if (fieldType === 'boolean' && value) {
              customFieldCounts[key] = (customFieldCounts[key] || 0) + 1
            } else if (fieldType === 'text' && value) {
              customFieldCounts[key] = (customFieldCounts[key] || 0) + 1
            }
          })
        }
      })
    } else if (tenant.type === TenantType.SERVICE) {
      // For service bookings, count unique services and employees
      const uniqueServices = new Set<string>()
      const uniqueEmployees = new Set<string>()

      bookingData.forEach((booking: BaseBookingModel) => {
        const serviceBooking = booking as ServiceBookingModel
        serviceBooking.services.forEach((service) => uniqueServices.add(service.id))
        if (serviceBooking.employee) {
          uniqueEmployees.add(serviceBooking.employee.id)
        }
      })

      totalPeople = uniqueServices.size // or employees, depending on what makes sense
      totalTables = uniqueEmployees.size
    }

    return {
      totalBookings,
      totalPeople,
      totalTables,
      customFieldCounts,
      tableSizeCounts,
    }
  }, [bookings, tenant, isUpdating])

  if (!stats || !tenant) return null

  // Check if statistics should be shown in dashboard
  if (!showStatistics) return null

  const enabledCustomFields =
    tenant.bookingCustomFields?.filter(
      (field: BookingCustomField) => statisticsConfig.enabledCustomFields?.[field.id] ?? true
    ) || []

  return (
    <div className="bg-muted/50 mb-2 flex items-center gap-2 rounded-lg p-2">
      <div className="flex flex-wrap items-center gap-2">
        {statisticsConfig.showTotalBookings && (
          <div className="flex items-center gap-1 text-sm">
            <span className="font-medium">Buchungen:</span>
            <span className="font-bold">{stats.totalBookings}</span>
          </div>
        )}

        {tenant.type === TenantType.FOOD && statisticsConfig.showPeople && (
          <div className="flex items-center gap-1 text-sm">
            <span className="font-medium">Gäste:</span>
            <span className="font-bold">{stats.totalPeople}</span>
          </div>
        )}

        {tenant.type === TenantType.FOOD && statisticsConfig.showTables && (
          <>
            <div className="flex items-center gap-1 text-sm">
              <span className="font-medium">Tische:</span>
              <span className="font-bold">{stats.totalTables}</span>
            </div>
          </>
        )}

        {enabledCustomFields.map((field: BookingCustomField) => {
          const count = stats.customFieldCounts[field.id] || 0
          return (
            <div key={field.id} className="flex items-center gap-1 text-sm">
              <span className="font-medium">{field.name}:</span>
              <span className="font-bold">{count}</span>
            </div>
          )
        })}
        {tenant.type === TenantType.FOOD &&
          statisticsConfig.showTableAggregation &&
          stats.tableSizeCounts &&
          Object.keys(stats.tableSizeCounts).length > 0 && (
            <div className="flex w-full items-center gap-1 text-sm">
              <span className="font-medium">Tischgrößen:</span>
              <span className="flex flex-wrap gap-1">
                {Object.entries(stats.tableSizeCounts)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([size, count]) => (
                    <Badge
                      key={size}
                      data-testid={`table-size-${size}`}
                      variant="secondary"
                      className="!border-1 !border-solid border-slate-400"
                    >
                      {count}×{size}er
                    </Badge>
                  ))}
              </span>
            </div>
          )}
      </div>
      <div className="mb-auto ml-auto flex h-full justify-start">
        <StatisticsConfigButton />
      </div>
    </div>
  )
}

export default StatisticSummary
